import React, { useState, useRef } from 'react';
import { Box, Typography, Card, CardContent, IconButton, Button, Stack, Tabs, Tab } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableCardOrig from './TableCard';
import SeatSelectModal from './SeatSelectModal';
import Draggable from 'react-draggable';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { API_ENDPOINTS } from '../../config/api';
import Checkbox from '@mui/material/Checkbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 1024;
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function getInitialZoom() {
  const stored = localStorage.getItem('seatingPlanZoom');
  if (stored) {
    const val = parseFloat(stored);
    if (ZOOM_LEVELS.includes(val)) return val;
  }
  return 0.75;
}

const DraggableTableOrig = ({ table, children, onMove, scale, selected, onSelect, multiDrag, setIsDragging, clearMarquee }) => {
  const handleStop = (e, data) => {
    setIsDragging(false); // Sürükleme bittiğinde isDragging'i false yap
    if (multiDrag && multiDrag.length > 1) {
      // Tüm seçili masaları aynı delta ile taşı
      const deltaX = data.x - (table.pos_x || 0);
      const deltaY = data.y - (table.pos_y || 0);
      multiDrag.forEach(t => {
        if (t.id !== table.id) {
          onMove && onMove(t.id, (t.pos_x || 0) + deltaX, (t.pos_y || 0) + deltaY);
        }
      });
      onMove && onMove(table.id, data.x, data.y);
    } else {
      onMove && onMove(table.id, data.x, data.y);
    }
  };
  return (
    <Draggable
      position={{ x: table.pos_x || 0, y: table.pos_y || 0 }}
      onStop={handleStop}
      onStart={() => { setIsDragging(true); clearMarquee && clearMarquee(); }} // Sürükleme başladığında marquee'yi de iptal et
      bounds="parent"
    >
      <div style={{ position: 'absolute', cursor: 'grab', border: selected ? '2px solid #1976d2' : '2px solid transparent', borderRadius: 8, background: selected ? 'rgba(25, 118, 210, 0.04)' : 'transparent' }}
        onMouseDown={e => e.stopPropagation()} // Masa kutusuna tıklama canvas'a gitmesin
      >
        <Checkbox
          checked={selected}
          onChange={() => onSelect(table.id)}
          size="small"
          sx={{ position: 'absolute', top: 4, left: 4, zIndex: 10, borderRadius: 1 }}
        />
        {React.cloneElement(children, { scale })}
      </div>
    </Draggable>
  );
};
const DraggableTable = React.memo(DraggableTableOrig);

const TableCanvas = ({ tables, setTables, participants, onTableEdit, onChange, codeHash, canvasHeight }) => {
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTable, setActiveTable] = useState(null);
  const [zoom, setZoom] = useState(getInitialZoom());
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [marquee, setMarquee] = useState(null); // {x1, y1, x2, y2}
  const marqueeRef = React.useRef(null); // performans için ref
  const canvasBoxRef = useRef();
  const token = localStorage.getItem('authToken');
  const [isDragging, setIsDragging] = useState(false);
  // Tab menü state
  const [tabValue, setTabValue] = React.useState('schema');
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getParticipant = (id) => participants.find(p => p.id === id);

  const handleSeatClick = (seat, table) => {
    setSelectedSeat({ ...seat, table });
    setActiveTable(table);
    setModalOpen(true);
  };

  const handleAssign = async (participant) => {
    if (!selectedSeat) return;
    await fetch(API_ENDPOINTS.seating.assignSeat(activeTable.codeHash || activeTable.event_id, selectedSeat.id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ participant_id: participant.id })
    });
    setModalOpen(false);
    // fetchData yerine sadece ilgili masanın seats'ini güncelle
    setTables(prev => prev.map(t =>
      t.id === activeTable.id
        ? { ...t, seats: t.seats.map(s => s.id === selectedSeat.id ? { ...s, participant_id: participant.id } : s) }
        : t
    ));
  };
  const handleUnassign = async () => {
    if (!selectedSeat) return;
    await fetch(API_ENDPOINTS.seating.unassignSeat(activeTable.codeHash || activeTable.event_id, selectedSeat.id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    setModalOpen(false);
    setTables(prev => prev.map(t =>
      t.id === activeTable.id
        ? { ...t, seats: t.seats.map(s => s.id === selectedSeat.id ? { ...s, participant_id: null } : s) }
        : t
    ));
  };

  const handleTableSelect = (tableId) => {
    setSelectedTableIds(prev => prev.includes(tableId)
      ? prev.filter(id => id !== tableId)
      : [...prev, tableId]
    );
  };
  // Tüm masalar seçili mi? (set olarak karşılaştır)
  const allSelected = tables.length > 0 && tables.length === selectedTableIds.length && tables.every(t => selectedTableIds.includes(t.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedTableIds([]);
    } else {
      setSelectedTableIds(tables.map(t => t.id));
    }
  };

  // Masa sürükle-bırak sonrası konum güncelle
  const handleMoveTable = async (tableId, newX, newY) => {
    const table = tables.find(t => t.id === tableId);
    const eventId = table?.codeHash || table?.event_id;
    if (!eventId) return;
    await fetch(API_ENDPOINTS.seating.update(eventId, tableId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ pos_x: newX, pos_y: newY })
    });
    // fetchData yerine sadece ilgili masanın konumunu güncelle
    setTables(prev => prev.map(t =>
      t.id === tableId ? { ...t, pos_x: newX, pos_y: newY } : t
    ));
  };

  // Masaları otomatik grid hizalama
  const handleAlignTables = async () => {
    if (tables.length === 0) return;
    setPrevTablePositions(tables.map(t => ({ id: t.id, pos_x: t.pos_x, pos_y: t.pos_y })));
    const spacingX = 200; // masa arası yatay boşluk
    const spacingY = 180; // masa arası dikey boşluk
    const startX = 60;
    const startY = 60;
    // Maksimum kolon sayısı 1024px genişliği aşmasın
    const maxCols = Math.max(1, Math.floor((1024 - startX) / spacingX));
    // Masaları order_no'ya göre sırala
    const sortedTables = [...tables].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
    const newTables = sortedTables.map((t, i) => {
      const row = Math.floor(i / maxCols);
      const col = i % maxCols;
      return {
        ...t,
        pos_x: startX + col * spacingX,
        pos_y: startY + row * spacingY
      };
    });
    setTables(newTables);
    // Backend'e pozisyonları güncelle
    for (let i = 0; i < newTables.length; i++) {
      const t = newTables[i];
      try {
        await fetch(`/api/tables/${t.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
          body: JSON.stringify({ pos_x: t.pos_x, pos_y: t.pos_y })
        });
      } catch (e) {
        // Hata olursa konsola yaz
        console.error('Masa pozisyonu güncellenemedi:', t.id, e);
      }
    }
  };

  // Masaları hizalamayı geri al
  const handleUndoAlign = () => {
    if (!prevTablePositions) return;
    setTables(tables => tables.map(t => {
      const prev = prevTablePositions.find(p => p.id === t.id);
      return prev ? { ...t, pos_x: prev.pos_x, pos_y: prev.pos_y } : t;
    }));
    setPrevTablePositions(null);
  };

  // PDF olarak dışa aktar
  const handleExportPDF = async () => {
    if (!canvasBoxRef.current) return;
    const canvas = await html2canvas(canvasBoxRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'px', [CANVAS_WIDTH, CANVAS_HEIGHT]);
    pdf.addImage(imgData, 'PNG', 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    pdf.save('oturma-plani.pdf');
  };

  // Yazdırma
  const handlePrint = () => {
    if (!canvasBoxRef.current) return;
    const printContents = canvasBoxRef.current.innerHTML;
    const win = window.open('', '', 'width=1200,height=900');
    win.document.write('<html><head><title>Oturma Planı</title>');
    win.document.write('<style>body{margin:0;}@media print{body{margin:0;}}</style>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const handleCanvasMouseDown = (e) => {
    if (isDragging) return; // Sürükleme sırasında marquee başlatma
    // Sadece sol tık
    if (e.button !== 0) return;
    const rect = canvasBoxRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    marqueeRef.current = { x1: x, y1: y, x2: x, y2: y };
    setMarquee({ x1: x, y1: y, x2: x, y2: y });
  };
  const handleCanvasMouseMove = (e) => {
    if (isDragging) return; // Masa taşınıyorsa seçim işlemi pasif
    if (!marqueeRef.current) return;
    const rect = canvasBoxRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    marqueeRef.current = { ...marqueeRef.current, x2: x, y2: y };
    setMarquee({ ...marqueeRef.current }); // throttle kaldırıldı
  };
  const handleCanvasMouseUp = () => {
    if (isDragging) return; // Masa taşınıyorsa seçim işlemi pasif
    if (!marqueeRef.current) return;
    const { x1, y1, x2, y2 } = marqueeRef.current;
    const xMin = Math.min(x1, x2);
    const xMax = Math.max(x1, x2);
    const yMin = Math.min(y1, y2);
    const yMax = Math.max(y1, y2);
    const newlySelected = tables.filter(t => {
      const tx = t.pos_x || 0;
      const ty = t.pos_y || 0;
      const width = t.type === 'dikdortgen' ? 220 * zoom : t.type === 'kare' ? 180 * zoom : 200 * zoom;
      const height = t.type === 'dikdortgen' ? 140 * zoom : t.type === 'kare' ? 180 * zoom : 200 * zoom;
      return (
        tx + width > xMin && tx < xMax && ty + height > yMin && ty < yMax
      );
    }).map(t => t.id);
    if (newlySelected.length === 0) {
      setSelectedTableIds([]);
    } else {
      setSelectedTableIds(prev => Array.from(new Set([...prev, ...newlySelected])));
    }
    setMarquee(null);
    marqueeRef.current = null;
  };

  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
    localStorage.setItem('seatingPlanZoom', newZoom);
  };

  // tables değiştiğinde seçili masaları güncelle
  React.useEffect(() => {
    setSelectedTableIds(prev => prev.filter(id => tables.some(t => t.id === id)));
  }, [tables]);

  // Marquee'yi iptal eden fonksiyon
  const clearMarquee = React.useCallback(() => {
    setMarquee(null);
    marqueeRef.current = null;
  }, []);

  // Masaları hizala işlemi için önceki pozisyonları sakla
  const [prevTablePositions, setPrevTablePositions] = React.useState(null);

  // Katılımcı raporu: toplam ve yerleştirilen
  // const totalParticipants = participants.length;
  // const assignedParticipants = tables.reduce((acc, t) => acc + (t.seats ? t.seats.filter(s => s.participant_id).length : 0), 0);

  return (
    <Box sx={{ minHeight: 500, bgcolor: '#fafbfc', borderRadius: 2, p: 2, boxShadow: 1, position: 'relative', overflow: 'auto', height: canvasHeight || 700 }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Oturma Planı
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 2, fontWeight: 400 }}>
            {/* kişi yerleştirildi raporu index.js'de */}
          </Typography>
        </Typography>
        <Button variant="outlined" onClick={handleSelectAll}>
          {allSelected ? 'Tümünü Bırak' : 'Tümünü Seç'}
        </Button>
        {/* <Button variant="outlined" onClick={handleAlignTables}>
          Masaları Hizala
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleUndoAlign} disabled={!prevTablePositions}>
          Geri Al
        </Button> */}
        <Button startIcon={<PictureAsPdfIcon />} color="secondary" onClick={handleExportPDF}>
          PDF Olarak İndir
        </Button>
      </Stack>
      {tabValue === 'schema' && (
        <Box
          ref={canvasBoxRef}
          sx={{
            position: 'relative',
            width: `${CANVAS_WIDTH}px`,
            height: `${CANVAS_HEIGHT}px`,
            minWidth: '300px',
            minHeight: '1024px',
            maxWidth: '1024px',
            border: '1px solid #ccc',
            mx: 'auto',
            bgcolor: '#f7f8fa',
            overflow: 'auto',
            boxShadow: 3,
            backgroundImage: `repeating-linear-gradient(0deg, #e0e0e0 0, #e0e0e0 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, #e0e0e0 0, #e0e0e0 1px, transparent 1px, transparent 40px)`
          }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={marquee ? handleCanvasMouseMove : undefined}
          onMouseUp={handleCanvasMouseUp}
        >
          {/* Marquee seçim kutusu */}
          {marquee && (
            <Box
              sx={{
                position: 'absolute',
                left: Math.min(marquee.x1, marquee.x2),
                top: Math.min(marquee.y1, marquee.y2),
                width: Math.abs(marquee.x2 - marquee.x1),
                height: Math.abs(marquee.y2 - marquee.y1),
                border: '2px dashed #1976d2',
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                pointerEvents: 'none',
                zIndex: 20
              }}
            />
          )}
          {/* Masalar */}
          {tables.map(table => (
            <DraggableTable
              key={table.id}
              table={table}
              onMove={handleMoveTable}
              scale={zoom}
              selected={selectedTableIds.includes(table.id)}
              onSelect={handleTableSelect}
              multiDrag={tables.filter(t => selectedTableIds.includes(t.id))}
              setIsDragging={setIsDragging}
              clearMarquee={clearMarquee}
            >
              <TableCardOrig
                table={table}
                orderNo={table.order_no}
                getParticipant={getParticipant}
                onSeatClick={seat => handleSeatClick(seat, table)}
                selectedSeatId={selectedSeat?.id}
                scale={zoom}
                onEdit={onTableEdit}
              />
            </DraggableTable>
          ))}
        </Box>
      )}
      {/* Diğer tab içerikleri burada olacak */}
      <SeatSelectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        participants={participants.filter(p => !tables.some(t => (t.seats || []).some(s => s.participant_id === p.id)) || selectedSeat?.participant_id === p.id)}
        seat={selectedSeat}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
      />
    </Box>
  );
};

export default TableCanvas;